import React, { useState } from "react";
import { Col, Row, Typography, Table, Input, Select, notification, Space, DatePicker, Tag, Grid } from "antd";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@hooks";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { claimsAPI } from "@api";
import { DateFormatMapping, OrderByDirection } from "@constants";

const { useBreakpoint } = Grid;
const { Search } = Input;
const { RangePicker } = DatePicker;

const OrderByField = {
  date: 'date',
  user_name: 'user_name',
  award_name: 'award_name',
  status: 'status',
  closed_date: 'closed_date',
}

const ClaimStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
}

const ClaimsPage = () => {
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    status: null,
    orderByField: null,
    orderByDirection: null,
    date: null,
    closedDate: null,
  });
  const [searchClaim, setSearchClaim] = useState("");
  const debouncedSearch = useDebounce(searchClaim, 300);
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("common");
  const { t: tError } = useTranslation("error");
  const { lg } = useBreakpoint();

  const statusOptions = [
    { value: ClaimStatus.PENDING, label: t("status.PENDING") },
    { value: ClaimStatus.IN_PROGRESS, label: t("status.IN_PROGRESS") },
    { value: ClaimStatus.DELIVERED, label: t("status.DELIVERED") },
    { value: ClaimStatus.CANCELLED, label: t("status.CANCELLED") },
  ];

  const { isLoading, data: claims } = useQuery({
    queryKey: ["claims", { ...params, search: debouncedSearch }],
    queryFn: () => claimsAPI.getClaims({
      ...params,
      date: params.date?.map(date => date.format('YYYY-MM-DD')),
      closedDate: params.closedDate?.map(date => date.format('YYYY-MM-DD')),
      search: debouncedSearch
    }).then(({ data }) => data),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate, isLoading: isUpdating } = useMutation({
    mutationFn: ({ id, status }) => {
      return claimsAPI.updateClaim(id, { status });
    },
    onSuccess: ({ data: [updatedClaim] }, variables) => {
      queryClient.setQueryData(["claims", { ...params, search: debouncedSearch }], (prev) => {
        return {
          ...prev,
          data: prev.data.map((item) =>
            item.id === variables.id ? { ...item, status: updatedClaim.status } : item
          )
        };
      });
      notification.success({
        message: t("claimTable.notification.status.success.title"),
        description: t("claimTable.notification.status.success.description"),
      });
    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const language = i18n.language.substring(0, 2);

  const columns = [
    {
      key: "date",
      title: t("claimTable.createdAt"),
      dataIndex: "createdAt",
      align: "center",
      width: 100,
      sorter: true,
      render: (date) => (date ? dayjs(date).locale(language).format("L") : "-"),
    },
    {
      key: "user_name",
      title: t("claimTable.user"),
      dataIndex: "user",
      width: 300,
      sorter: true,
      render: (user) => user.fullName,
    },
    {
      key: "award_name",
      title: t("claimTable.award"),
      dataIndex: "award",
      width: 300,
      sorter: true,
      render: (award) => award.name,
    },
    {
      key: "status",
      title: t("claimTable.status"),
      dataIndex: "status",
      align: "center",
      width: 150,
      sorter: true,
      render: (status, { id }) => !(status === ClaimStatus.DELIVERED || status === ClaimStatus.CANCELLED) ? (
        <Select
          defaultValue={status}
          onChange={(newStatus) => {
            mutate({ id, status: newStatus });
          }}
          style={{ width: "150px" }}
          options={statusOptions}
          disabled={isUpdating}
        />
      ) : (
        <Tag color={status === ClaimStatus.DELIVERED ? "success" : "error"}>
          {status === ClaimStatus.DELIVERED ? t("status.DELIVERED") : t("status.CANCELLED")}
        </Tag>
      ),
    },
    {
      key: "closed_date",
      title: t("claimTable.closed"),
      dataIndex: "closed",
      align: "center",
      width: 100,
      sorter: true,
      render: (date) => (date ? dayjs(date).locale(language).format("L") : "-"),
    },
  ];

  return (
    <>
      <Row justify="center" align="middle" style={{ maxWidth: 1300, margin: "0px auto", padding: 10, background: "white" }}>
        <Col span={22}>
          <Typography.Title align="left" level={2} style={{ marginTop: 0 }}>
            {t("navbar.claims")}
          </Typography.Title>
        </Col>
        <Col
          span={22}
          align="end"
          style={{
            borderTopLeftRadius: "5px",
            borderTopRightRadius: "5px",
            overflow: "hidden",
            width: "100%",
            padding: "16px 0px",
            display: "flex",
            justifyContent: lg ? "flex-start" : "center",
            columnGap: "16px",
            rowGap: "10px",
            flexWrap: "wrap",
            background: "#fff",
          }}
        >
          <Space
            direction="vertical"
            style={{
              textAlign: "left",
              flexGrow: 1,
            }}
          >
            <Typography.Text>{t("claimTable.search")}</Typography.Text>
            <Search
              onChange={(e) => setSearchClaim(e.target.value)}
              style={{
                width: "100%",
                maxWidth: "400px",
              }}
              allowClear
              placeholder={t("claimTable.search")}
            />
          </Space>
          <Space
            direction="vertical"
            style={{
              textAlign: "left",
              width: lg ? 200 : 280
            }}
          >
            <Typography.Text>{t("claimTable.status")}</Typography.Text>
            <Select
              onChange={(value) => setParams({
                ...params,
                status: value
              })}
              style={{
                width: "100%",
                maxWidth: lg ? 200 : 280,
              }}
              options={statusOptions}
              allowClear
              placeholder={t("claimTable.status")}
            />
          </Space>
          <Space
            direction="vertical"
            style={{
              textAlign: "left"
            }}
          >
            <Typography.Text>{t("redeemTable.filter.date")}</Typography.Text>
            <RangePicker
              onChange={(dates) => {
                setParams({
                  ...params,
                  date: dates,
                })
              }}
              format={DateFormatMapping[language]}
            />
          </Space>
          <Space
            direction="vertical"
            style={{
              textAlign: "left"
            }}
          >
            <Typography.Text>{t("redeemTable.filter.closed")}</Typography.Text>
            <RangePicker
              onChange={(dates) => setParams({
                ...params,
                closedDate: dates,
              })}
              format={DateFormatMapping[language]}
            />
          </Space>
        </Col>
        <Col
          span={22}
          align="center"
          style={{
            height: "100%",
            backgroundColor: "#fff",
          }}
        >
          <Table
            dataSource={claims?.data ?? []}
            columns={columns}
            pagination={{
              current: params.page,
              pageSize: params.limit,
              total: claims?.count,
              position: ["bottomCenter"],
              size: "default"
            }}
            rowKey={"id"}
            scroll={{ x: "max-content", y: "calc(100vh - 335px)" }}
            size="small"
            loading={isLoading}
            borderRadius={false}
            onChange={(pagination, filters, sorter) => {
              setParams({
                ...params,
                page: pagination.current,
                limit: pagination.pageSize,
                orderByField: OrderByField[sorter.columnKey],
                orderByDirection: OrderByDirection[sorter.order]
              })
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default ClaimsPage;
